import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Folder as FolderIcon,
  FolderShared as FolderSharedIcon,
  Search as SearchIcon,
  Label as LabelIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { Collection, CreateCollectionDTO } from '../models/Collection';
import { Group } from '../models/Group';
import { useMyCollections, useAccessibleCollections, useMyGroups, useCollectionLabels, useCreateCollection } from '../hooks/useQueries';
import CollectionThumbnails from '../components/CollectionThumbnails';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#795548', '#607D8B', '#F44336', '#3F51B5',
];

const Collections: React.FC = () => {
  const navigate = useNavigate();
  
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCollection, setNewCollection] = useState<CreateCollectionDTO>({
    name: '',
    description: '',
    color: COLORS[0],
    isShared: false,
    labels: [],
  });
  const [newLabel, setNewLabel] = useState('');

  const { data: myCollectionsData = [], isLoading: myLoading } = useMyCollections();
  const { data: accessibleData = [], isLoading: accLoading } = useAccessibleCollections();
  const { data: myGroups = [] } = useMyGroups();
  const { data: labels = [] } = useCollectionLabels();
  const createMutation = useCreateCollection();

  const loading = myLoading || accLoading;
  const myCollections = myCollectionsData;
  const sharedCollections = accessibleData.filter(c => !myCollectionsData.some(m => m.id === c.id));

  const handleCreateCollection = async () => {
    if (!newCollection.name.trim()) return;
    
    try {
      const created = await createMutation.mutateAsync(newCollection);
      setCreateDialogOpen(false);
      setNewCollection({
        name: '',
        description: '',
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        isShared: false,
        labels: [],
      });
      navigate(`/collections/${created.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create collection');
    }
  };

  const handleAddLabel = () => {
    if (newLabel.trim() && !newCollection.labels?.includes(newLabel.trim())) {
      setNewCollection({
        ...newCollection,
        labels: [...(newCollection.labels || []), newLabel.trim()],
      });
      setNewLabel('');
    }
  };

  const handleRemoveLabel = (label: string) => {
    setNewCollection({
      ...newCollection,
      labels: newCollection.labels?.filter(l => l !== label) || [],
    });
  };

  const filteredMyCollections = myCollections.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.labels?.some(l => l.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredSharedCollections = sharedCollections.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.labels?.some(l => l.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const CollectionCard: React.FC<{ collection: Collection }> = ({ collection }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': { boxShadow: 6 },
        borderLeft: `4px solid ${collection.color || '#2196F3'}`,
      }}
      onClick={() => navigate(`/collections/${collection.id}`)}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: collection.color || 'primary.main', mr: 2 }}>
            {collection.isShared ? <FolderSharedIcon /> : <FolderIcon />}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap>{collection.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {collection.imageCount} images
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
          {collection.description || 'No description'}
        </Typography>

        {collection.groupName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <GroupIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {collection.groupName}
            </Typography>
          </Box>
        )}

        {collection.labels && collection.labels.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {collection.labels.slice(0, 3).map(label => (
              <Chip key={label} icon={<LabelIcon />} label={label} size="small" variant="outlined" />
            ))}
            {collection.labels.length > 3 && (
              <Chip label={`+${collection.labels.length - 3}`} size="small" />
            )}
          </Box>
        )}

        {collection.imageCount > 0 && (
          <CollectionThumbnails collectionId={collection.id} maxImages={4} />
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Collections
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => setCreateDialogOpen(true)}
          sx={{ 
            minHeight: 44,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          New Collection
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search collections..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: { xs: 2, sm: 3 } }}
        inputProps={{ style: { fontSize: 16 } }}
      />

      <Tabs 
        value={tabValue} 
        onChange={(_, v) => setTabValue(v)} 
        sx={{ 
          mb: 2,
          '& .MuiTab-root': {
            minHeight: { xs: 48, sm: 48 },
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
          }
        }}
        variant="fullWidth"
      >
        <Tab label={`My Collections (${filteredMyCollections.length})`} />
        <Tab label={`Shared with Me (${filteredSharedCollections.length})`} />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {filteredMyCollections.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {searchTerm ? 'No collections match your search' : 'No collections yet'}
            </Typography>
            {!searchTerm && (
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setCreateDialogOpen(true)}>
                Create Your First Collection
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
            {filteredMyCollections.map(collection => (
              <Box key={collection.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.33% - 16px)' } }}>
                <CollectionCard collection={collection} />
              </Box>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {filteredSharedCollections.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FolderSharedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {searchTerm ? 'No shared collections match your search' : 'No collections shared with you'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
            {filteredSharedCollections.map(collection => (
              <Box key={collection.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.33% - 16px)' } }}>
                <CollectionCard collection={collection} />
              </Box>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Create Collection Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          }
        }}
      >
        <DialogTitle>Create New Collection</DialogTitle>
        <DialogContent sx={{ pb: { xs: 2, sm: 3 } }}>
          <TextField
            autoFocus
            margin="dense"
            label="Collection Name"
            fullWidth
            value={newCollection.name}
            onChange={(e) => setNewCollection({ ...newCollection, name: e.target.value })}
            required
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={newCollection.description}
            onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <TextField
            margin="dense"
            label="Research Paper URL"
            fullWidth
            placeholder="https://..."
            value={newCollection.researchPaperUrl || ''}
            onChange={(e) => setNewCollection({ ...newCollection, researchPaperUrl: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />

          <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {COLORS.map(color => (
              <Box
                key={color}
                sx={{
                  width: { xs: 40, sm: 32 },
                  height: { xs: 40, sm: 32 },
                  borderRadius: '50%',
                  bgcolor: color,
                  cursor: 'pointer',
                  border: newCollection.color === color ? '3px solid black' : 'none',
                }}
                onClick={() => setNewCollection({ ...newCollection, color })}
              />
            ))}
          </Box>

          <Typography variant="body2" sx={{ mb: 1 }}>Labels</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              size="small"
              placeholder="Add label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddLabel()}
              fullWidth
              inputProps={{ style: { fontSize: 16 } }}
            />
            <Button 
              onClick={handleAddLabel} 
              disabled={!newLabel.trim()}
              sx={{ minHeight: 40, width: { xs: '100%', sm: 'auto' } }}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            {newCollection.labels?.map(label => (
              <Chip
                key={label}
                label={label}
                onDelete={() => handleRemoveLabel(label)}
                size="small"
              />
            ))}
          </Box>

          {myGroups.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>Share with Group (optional)</Typography>
              <Autocomplete
                options={myGroups}
                getOptionLabel={(option) => option.name}
                onChange={(_, value) => setNewCollection({
                  ...newCollection,
                  groupId: value?.id,
                  isShared: !!value,
                })}
                renderInput={(params) => <TextField {...params} placeholder="Select a group" size="small" />}
                sx={{ mb: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCollection}
            variant="contained"
            disabled={!newCollection.name.trim() || createMutation.isPending}
            sx={{ minHeight: 44 }}
          >
            {createMutation.isPending ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Collections;
